import { useQuery,  } from '@tanstack/react-query';

import { useMemo,  } from 'react';

import {
	InventoryListingStatus,
	InventoryMatchingStatus
} from '@ekhodealer/ekho-common';

import type { 	AssignmentStatus,
	InventoryItemCondition,
InventoryItemSearchRecord, PaginatedResponse ,
	SyncStatus} from '@ekhodealer/ekho-common';
import type {
	OnlineSalesOption,
	PublishStatusOption,
	VehicleMatchOption,
} from '../atoms/inventoryAtoms';
import type { GetInventoryParams } from '../lib/types';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

// Re-export types for external consumers
export type { FrontendInventoryFilters, GetInventoryParams } from '../lib/types';

/**
 * Backend query parameters format (flat structure expected by the API)
 * Matches the InventoryQuerySchema in inventory.schemas.ts
 * Array fields should always be sent as arrays (even single values as [value])
 * Index signature allows compatibility with Record<string, unknown>
 */
interface BackendQueryParams {
	[key: string]: string | string[] | undefined;
	limit?: string;
	page?: string;
	search?: string;
	// Array fields - always send as arrays
	assignmentStatus?: AssignmentStatus[];
	'listing.status'?: InventoryListingStatus[];
	'matching.status'?: InventoryMatchingStatus[];
	syncStatus?: SyncStatus[];
	isMarkedForOnlineSale?: string;
	// Multi-select filters (always arrays)
	make?: string[];
	model?: string[];
	vehicleCategory?: string[];
	newUsed?: InventoryItemCondition[];
	importJobId?: string;
	// Facets to return
	facets?: string[];
}

/**
 * Maps frontend publishStatus values to backend InventoryListingStatus
 * Note: draft includes DRAFT, INCOMPLETE_DRAFT, and LOCKED statuses
 */
const mapPublishStatusToListingStatus = (
	values: PublishStatusOption[],
): InventoryListingStatus[] | undefined => {
	if (values.length === 0) return undefined;

	const mapping: Record<PublishStatusOption, InventoryListingStatus | InventoryListingStatus[]> = {
		published: InventoryListingStatus.PUBLISHED,
		draft: [
			InventoryListingStatus.DRAFT,
			InventoryListingStatus.INCOMPLETE_DRAFT,
			InventoryListingStatus.LOCKED,
		],
		hidden: InventoryListingStatus.HIDDEN,

	};

	// Collect all statuses for selected values and flatten
	const statuses = values.flatMap((value) => {
		const mapped = mapping[value];
		return Array.isArray(mapped) ? mapped : [mapped];
	});
	return statuses.length > 0 ? statuses : undefined;
};

/**
 * Maps frontend vehicleMatch values to backend InventoryMatchingStatus
 */
const mapVehicleMatchToMatchingStatus = (
	values: VehicleMatchOption[],
): InventoryMatchingStatus[] | undefined => {
	if (values.length === 0) return undefined;

	const mapping: Record<VehicleMatchOption, InventoryMatchingStatus | InventoryMatchingStatus[]> =
		{
			'in-progress': [
				InventoryMatchingStatus.ENRICHED,
				InventoryMatchingStatus.VEHICLE_MATCHED,
				InventoryMatchingStatus.OEM_MATCHED,
			],
			complete: InventoryMatchingStatus.MATCHED,
		};

	// Collect all statuses for selected values and flatten
	const statuses = values.flatMap((value) => mapping[value]);
	return statuses.length > 0 ? statuses : undefined;
};

/**
 * Maps frontend onlineSales values to backend isMarkedForOnlineSale boolean
 */
const mapOnlineSalesFilter = (values: OnlineSalesOption[]): string | undefined => {
	if (values.length === 0) return undefined;

	// 'enabled' means isMarkedForOnlineSale = true
	// 'lead-gen' means isMarkedForOnlineSale = false
	if (values.includes('enabled')) return 'true';
	if (values.includes('lead-gen')) return 'false';

	return undefined;
};

/**
 * Transforms frontend filter params to backend query params format
 */
const transformFiltersToQueryParams = (params: GetInventoryParams): BackendQueryParams => {
	const queryParams: BackendQueryParams = {};

	// Add pagination
	if (params.limit !== undefined) {
		queryParams.limit = params.limit.toString();
	}
	if (params.page !== undefined) {
		queryParams.page = params.page.toString();
	}

	// Add search
	if (params.query) {
		queryParams.search = params.query;
	}

	// Add facets if present
	if (params.facets && params.facets.length > 0) {
		queryParams.facets = params.facets;
	}

	// Transform filters if present
	if (params.filters) {
		const { filters } = params;

		// Map publishStatus to listing.status
		// Always exclude archived items from results
		if (filters.publishStatus && filters.publishStatus.length > 0) {
			const status = mapPublishStatusToListingStatus(filters.publishStatus);
			if (status) queryParams['listing.status'] = status;
		} else {
			// If no publishStatus filter is set, exclude archived by default
			// Include all other statuses: PUBLISHED, HIDDEN, DRAFT, INCOMPLETE_DRAFT, LOCKED
			// Note: INCOMPLETE_DRAFT and LOCKED are treated as draft statuses
			queryParams['listing.status'] = [
				InventoryListingStatus.PUBLISHED,
				InventoryListingStatus.HIDDEN,
				InventoryListingStatus.DRAFT,
				InventoryListingStatus.INCOMPLETE_DRAFT,
				InventoryListingStatus.LOCKED,
			];
		}

		// Map vehicleMatch to matchingStatus
		if (filters.vehicleMatch && filters.vehicleMatch.length > 0) {
			const status = mapVehicleMatchToMatchingStatus(filters.vehicleMatch);
			if (status) queryParams['matching.status'] = status;
		}

		// Map onlineSales to isMarkedForOnlineSale
		if (filters.onlineSales && filters.onlineSales.length > 0) {
			const onlineSale = mapOnlineSalesFilter(filters.onlineSales);
			if (onlineSale) queryParams.isMarkedForOnlineSale = onlineSale;
		}

		// Pass through direct filters
		if (filters.make && filters.make.length > 0) {
			queryParams.make = filters.make;
		}
		if (filters.model && filters.model.length > 0) {
			queryParams.model = filters.model;
		}
		if (filters.vehicleCategory && filters.vehicleCategory.length > 0) {
			queryParams.vehicleCategory = filters.vehicleCategory;
		}
		if (filters.newUsed && filters.newUsed.length > 0) {
			queryParams.newUsed = filters.newUsed as InventoryItemCondition[];
		}

		// Map importJobId to importJobId
		if (filters.importJobId) {
			queryParams.importJobId = filters.importJobId;
		}
	}

	return queryParams;
};

/**
 * API function to get all inventory items with optional filters and pagination
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional filters and pagination parameters
 */
export const getInventory = async (
	servicesProviderId: string,
	params: GetInventoryParams,
): Promise<PaginatedResponse<InventoryItemSearchRecord>> => {
	// Transform frontend params to backend query format
	const queryParams = transformFiltersToQueryParams(params);

	const response = await apiClient.get<PaginatedResponse<InventoryItemSearchRecord>>(
		inventoryEndpoints.base(servicesProviderId),
		{
			params: queryParams,
		},
	);
	return response.data;
};

/**
 * Hook for fetching inventory items with optional filters and pagination
 */
export const useGetInventory = (params: GetInventoryParams) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const currentServicesProviderId = useCurrentServicesProviderId();
	// const queryClient = useQueryClient();

	const queryKey = useMemo(() => {
		if (!currentServicesProviderId) {
			return ['inventory', 'list', 'missing-owner'];
		}

		return queries.inventory.list(currentServicesProviderId, params).queryKey;
	}, [params, currentServicesProviderId]);

	const query = useQuery({
		queryKey,
		queryFn: async () => {
			if (!currentServicesProviderId) {
				throw new Error('Unable to fetch inventory without a services provider ID.');
			}

			const response = await getInventory(currentServicesProviderId, params);

			return response;
		},
		enabled: Boolean(currentServicesProviderId),
		staleTime: Infinity, // never stale unless the data is mutated
		...errorBoundaryOptions,
	});

	// // Populate individual item caches after query completes successfully
	// // This runs in an effect to ensure it happens after the query settles
	// useEffect(() => {
	// 	if (query.data?.data) {
	// 		query.data.data.forEach((item) => {
	// 			queryClient.setQueryData(queries.inventory.detail(item.id).queryKey, item);
	// 		});
	// 	}
	// }, [query.data, queryClient]);

	return query;
};
